/* eslint-disable max-classes-per-file */
import { Controller } from '@hotwired/stimulus';

class PassengerRow {
  constructor(element, initialAge, updateCallback) {
    if (initialAge === undefined) {
      this.age = null;
    } else {
      this.age = initialAge;
    }
    this.element = element;
    this.updateCallback = updateCallback;
    this.labelElement = this.element.querySelector('label');
    this.removeButtonElement = this.element.querySelector('button');
    this.inputElement = this.element.querySelector('input[type=number]');
    this.inputElement.value = String(this.age);

    this.selectInitial();
    this.updateCategory();

    this.element.querySelector('select').addEventListener('change', () => this.update());
    this.inputElement.addEventListener('change', () => this.updateAge());
  }

  update() {
    this.inputElement.value = null;

    this.updateCategory();
    this.updateAge();
  }

  updateCategory() {
    const option = this.currentOptionElement();

    if (option.dataset.default === '1') {
      this.inputElement.classList.add('hidden');
    } else {
      const min = Number(option.dataset.min);
      const max = Number(option.dataset.max);

      this.inputElement.classList.remove('hidden');
      this.inputElement.min = min;
      this.inputElement.max = max;
    }
  }

  updateAge() {
    const age = this.inputElement.value;
    if (age === '') {
      this.age = null;
    } else {
      this.age = Number(age);
    }

    this.updateCallback(this);
  }

  updateIndex(index, showRemove) {
    this.removeButtonElement.dataset.index = index;
    if (showRemove) {
      this.removeButtonElement.classList.remove('hidden');
    } else {
      this.removeButtonElement.classList.add('hidden');
    }

    this.labelElement.innerText = index + 1;
  }

  destroy() {
    this.element.parentNode.removeChild(this.element);
  }

  selectInitial() {
    if (this.age === null) {
      this.selectDefault();
    } else {
      this.selectAge();
    }
  }

  selectDefault() {
    Array.from(this.element.querySelectorAll('option')).forEach((option) => {
      option.selected = option.dataset.default === '1';
    });
  }

  selectAge() {
    Array.from(this.element.querySelectorAll('option')).forEach((option) => {
      const min = Number(option.dataset.min);
      const max = Number(option.dataset.max);
      option.selected = min <= this.age && (Number.isNaN(max) || max >= this.age);
    });
  }

  currentOptionElement() {
    return this.element.querySelector('select').selectedOptions[0];
  }
}

export default class PassengersController extends Controller {
  static targets = ['passengersField', 'passenger', 'passengerTemplate', 'passengerList', 'addPassenger', 'passengersLabel'];

  initialize() {
    this.passengerRows = [];
    this.ageUpdateCallback = () => this.updateField();
  }

  connect() {
    const passengerParams = this.passengersFieldTarget.value.split(',');
    passengerParams.forEach((param) => {
      let age;
      if (param === 'A') {
        age = null;
      } else {
        age = Number(param);
      }

      this.createPassengerContainer(age);
    });

    this.updateIndices();
  }

  disconnect() {
    this.passengerRows.forEach((row) => {
      row.destroy();
    });
    this.passengerRows = [];
  }

  addPassenger() {
    this.createPassengerContainer();

    this.updateIndices();
    this.updateField();
  }

  removePassenger(event) {
    const removedRow = this.passengerRows.splice(event.currentTarget.dataset.index, 1)[0];
    removedRow.destroy();

    this.updateIndices();
    this.updateField();
  }

  updateIndices() {
    const passengerCount = this.passengerRows.length;
    let index = 0;
    const showRemove = passengerCount > 1;
    this.passengerRows.forEach((row) => {
      row.updateIndex(index, showRemove);
      index += 1;
    });

    if (this.passengerRows.length >= 9) {
      this.addPassengerTarget.classList.add('hidden');
    } else {
      this.addPassengerTarget.classList.remove('hidden');
    }

    if (passengerCount === 1) {
      this.passengersLabelTarget.innerText = `${passengerCount} ${this.passengersLabelTarget.dataset.passengersOne}`;
    } else {
      this.passengersLabelTarget.innerText = `${passengerCount} ${this.passengersLabelTarget.dataset.passengersOther}`;
    }
  }

  updateField() {
    const params = this.passengerRows.map((row) => {
      if (row.age == null) {
        return 'A';
      }

      return String(row.age);
    });

    this.passengersFieldTarget.value = params.join(',');
  }

  // Internal

  createPassengerContainer(age) {
    const container = document.createElement('div');
    container.dataset.passengersTarget = 'passenger';
    container.appendChild(this.passengerTemplateTarget.content.cloneNode(true));

    this.passengerRows.push(new PassengerRow(container, age, this.ageUpdateCallback));
    this.passengerListTarget.appendChild(container);
  }
}
